import React, { useEffect, useRef, useState } from "react";

import {
  useMarketplaceFacilitators,
  type FacilitatorStatus,
} from "@/hooks/useMarketplaceFacilitators";

import { FacilitatorChip } from "./FacilitatorChip";
import { InlineError } from "./InlineError";
import { MarkFacilitatorMenu } from "./MarkFacilitatorMenu";
import { RemoveFacilitatorModal } from "./RemoveFacilitatorModal";

const MENU = "menu";

export const SellerFacilitators: React.FC<{ workspaceId: string }> = ({
  workspaceId,
}) => {
  const { rows, loadFailed, mark, remove } =
    useMarketplaceFacilitators(workspaceId);
  const [busyCode, setBusyCode] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [confirming, setConfirming] = useState<FacilitatorStatus | null>(null);
  const [focusNext, setFocusNext] = useState<string | null>(null);
  const menuRef = useRef<HTMLButtonElement>(null);
  const chipRefs = useRef<Record<string, HTMLButtonElement | null>>({});

  useEffect(() => {
    if (!focusNext) return;
    (chipRefs.current[focusNext] ?? menuRef.current)?.focus();
    setFocusNext(null);
  }, [focusNext, rows]);

  const run = async (
    code: string,
    action: () => Promise<string | null>,
    focusAfter: (ok: boolean) => string,
  ) => {
    if (busyCode) return;
    setBusyCode(code);
    setError(null);
    const message = await action();
    setBusyCode(null);
    if (message) setError(message);
    else setConfirming(null);
    if (!message || !confirming) setFocusNext(focusAfter(!message));
  };

  const removeRow = (row: FacilitatorStatus) => {
    const { markedId } = row;
    const code = row.marketplaceCode;
    if (markedId)
      run(
        code,
        () => remove(markedId),
        (ok) => (ok ? MENU : code),
      );
  };

  const cancelRemove = () => {
    setError(null);
    setConfirming(null);
  };

  const askRemove = (row: FacilitatorStatus) => {
    if (row.linkedProducts + row.openOrders + row.sellers === 0)
      return removeRow(row);
    setError(null);
    setConfirming(row);
  };

  if (loadFailed)
    return (
      <InlineError
        message="Could not load facilitator status. Please refresh the page."
        className="mb-6"
      />
    );
  if (rows.length === 0) return null;

  const marked = rows.filter((row) => row.markedId);
  const unmarked = rows.filter((row) => !row.markedId && row.connected);

  return (
    <div className="mb-6">
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-xs font-medium text-slate-500 dark:text-slate-400">
          Facilitator:
        </span>
        {marked.map((row) => (
          <FacilitatorChip
            key={row.marketplaceCode}
            row={row}
            busy={busyCode === row.marketplaceCode}
            removeRef={(el) => {
              chipRefs.current[row.marketplaceCode] = el;
            }}
            onRemove={() => askRemove(row)}
          />
        ))}
        {unmarked.length > 0 && (
          <MarkFacilitatorMenu
            rows={unmarked}
            busy={unmarked.some((row) => row.marketplaceCode === busyCode)}
            disabled={busyCode !== null}
            triggerRef={menuRef}
            onMark={(code) =>
              run(
                code,
                () => mark(code),
                (ok) => (ok ? code : MENU),
              )
            }
          />
        )}
      </div>
      {error && !confirming && (
        <InlineError message={error} className="mt-2 max-w-none" />
      )}
      <RemoveFacilitatorModal
        row={confirming}
        error={confirming ? (error ?? undefined) : undefined}
        onConfirm={() => confirming && removeRow(confirming)}
        onCancel={cancelRemove}
      />
    </div>
  );
};
